import { Badge, Button, Divider, Group, NumberInput, Switch, Text, TextInput, Textarea, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useVariables } from '../../hooks/useVariables';
import { EditorItem, TextStyle, useEditorStore } from '../../store/editorStore';

type AutoImageItemPanelProps = {
  item: EditorItem;
  keywordInputRef: React.RefObject<HTMLInputElement>;
  handleKeywordUpdate: (value: string) => void;
  handleKeywordFocus: (e?: React.FocusEvent) => void;
  handleKeywordBlur: (e?: React.FocusEvent) => void;
  handleStyleUpdate: (styleUpdates: Partial<TextStyle>) => void;
};

export function AutoImageItemPanel({ 
  item, 
  keywordInputRef,
  handleKeywordUpdate,
  handleKeywordFocus,
  handleKeywordBlur,
  handleStyleUpdate
}: AutoImageItemPanelProps) {
  const updateItem = useEditorStore((state) => state.updateItem);
  const { variables } = useVariables();
  
  // Xử lý khi chèn biến vào prompt
  const handleInsertVariable = (variableName: string) => {
    const newContent = `${item.content} {{${variableName}}}`;
    handleKeywordUpdate(newContent);
    
    notifications.show({
      title: 'Đã chèn biến',
      message: `Đã chèn biến {{${variableName}}} vào prompt`,
      color: 'blue',
      autoClose: 2000,
    });
  };

  // Chuyển đổi loại item
  const switchItemType = (newType: 'auto-image' | 'auto-image-prompt') => {
    // Lưu lại giá trị content hiện tại
    const currentContent = item.content;
    
    // Cập nhật loại item
    updateItem(item.id, { 
      type: newType,
      // Giữ nguyên content
    });
  };

  return (
    <>
      <Title order={4}>Thuộc tính</Title>
      
      <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
        <Button 
          variant={item.type === 'auto-image' ? 'filled' : 'outline'}
          onClick={() => switchItemType('auto-image')}
          style={{ flex: 1 }}
        >
          Tìm kiếm bằng từ khóa
        </Button>
        <Button 
          variant={item.type === 'auto-image-prompt' ? 'filled' : 'outline'}
          onClick={() => switchItemType('auto-image-prompt')}
          style={{ flex: 1 }}
        >
          Sinh từ khóa bằng prompt
        </Button>
      </div>
      
      {item.type === 'auto-image' && (
        <TextInput
          ref={keywordInputRef}
          label="Từ khóa tìm kiếm"
          value={item.content}
          onChange={(e) => handleKeywordUpdate(e.target.value)}
          placeholder="Nhập từ khóa để tìm ảnh"
          onKeyDown={(e) => {
            // Cho phép Ctrl+A hoạt động để chọn tất cả
            if (e.key === 'a' && (e.ctrlKey || e.metaKey)) {
              return;
            }
            
            e.stopPropagation();
            
            // Chỉ ngăn xóa nếu nội dung trống
            if ((e.key === 'Delete' || e.key === 'Backspace') && !item.content) {
              e.preventDefault();
            }
          }}
          onFocus={handleKeywordFocus}
          onBlur={handleKeywordBlur}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        />
      )}
            
      {item.type === 'auto-image-prompt' && (
        <>
          <Textarea
            label="Prompt sinh từ khóa"
            placeholder="Nhập prompt để sinh từ khóa tìm kiếm..."
            value={item.content}
            onChange={(e) => handleKeywordUpdate(e.currentTarget.value)}
            minRows={6}
            maxRows={10}
            mb="xs"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          />
          
          {variables.length > 0 && (
            <Group mb="md">
              {variables.map((variable, index) => (
                <Button
                  key={index}
                  variant="outline"
                  size="xs"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleInsertVariable(variable.name);
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  rightSection={
                    variable.value ? (
                      <Badge 
                        color="gray" 
                        variant="light" 
                        size="xs"
                        title={`Giá trị: ${variable.value}`}
                      >
                        {variable.value.substring(0, 8)}{variable.value.length > 8 ? '...' : ''}
                      </Badge>
                    ) : null
                  }
                >
                  {variable.name}
                </Button>
              ))}
            </Group>
          )}
        </>
      )}
      
      <Divider my="md" />
      
      <Title order={5}>Tùy chọn xử lý ảnh</Title>
      <Switch
        label="Xóa nền ảnh"
        checked={item.style.removeBackground || false}
        onChange={(e) => handleStyleUpdate({ removeBackground: e.currentTarget.checked })}
      />
      
      <TextInput
        label="Màu nền (mã màu)"
        value={item.style.backgroundColor || 'transparent'}
        onChange={(e) => handleStyleUpdate({ backgroundColor: e.target.value })}
        disabled={!(item.style.removeBackground || false)}
        placeholder="#RRGGBB, transparent hoặc tên màu"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      />
      
      <Divider my="sm" label="Viền ô chứa" labelPosition="center" />
      
      <NumberInput
        label="Độ dày viền ô"
        value={item.style.borderWidth || 0}
        onChange={(value) => {
          if (typeof value === 'number') {
            handleStyleUpdate({ borderWidth: value });
          }
        }}
        min={0}
        max={20}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      />
      
      <TextInput
        label="Màu viền ô"
        value={item.style.borderColor || '#000000'}
        onChange={(e) => handleStyleUpdate({ borderColor: e.target.value })}
        placeholder="#RRGGBB hoặc tên màu"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        disabled={(item.style.borderWidth || 0) <= 0}
      />
      
      <NumberInput
        label="Độ mờ (blur)"
        value={item.style.blur || 0}
        onChange={(value) => {
          if (typeof value === 'number') {
            handleStyleUpdate({ blur: value });
          }
        }}
        min={0}
        max={20}
        step={0.5}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      />
      
      <Text size="xs" color="dimmed" mt="md">
        {item.type === 'auto-image' 
          ? 'Ảnh sẽ được tải xuống dựa trên từ khóa tìm kiếm' 
          : 'Ảnh sẽ được tải xuống dựa trên từ khóa sinh ra từ prompt'}
      </Text>
      
      <Divider my="md" />
      
      {/* Điều khiển lớp (zIndex) */}
      <NumberInput
        label="Lớp hiển thị (zIndex)"
        value={item.zIndex}
        onChange={(value) => {
          if (typeof value === 'number') {
            updateItem(item.id, { zIndex: value });
          }
        }}
        min={0}
        max={999}
        step={1}
        hideControls={false}
        allowDecimal={false}
        clampBehavior="strict"
        description="Giá trị càng cao, phần tử càng hiển thị phía trên"
      />
    </>
  );
} 